import { createTheme } from '@mui/material/styles';

// Related to Component Search's sky blues, with deeper actions and a teal accent.
export const colors = {
  primary: '#2d62b0',
  primaryHover: '#234d8c',
  accent: '#087f8c',
  accentHover: '#076773',
  secondary: '#52677f',
  secondaryHover: '#40536a',
  success: '#18765c',
  successHover: '#125d48',
  warning: '#e4bc64',
  warningHover: '#d7ab48',
  warningText: '#71500d',
  danger: '#b54748',
  dangerHover: '#933638',
  ink: '#203752',
  muted: '#52677f',
  background: '#f3f6fb',
  surface: '#ffffff',
  surfaceTint: '#edf3fb',
  border: '#d5e0ed',
  controlBorder: '#8295ad',
  headerStart: '#d2e4fb',
  headerEnd: '#e1f1f0',
  navy: '#192f4c',
  footerMuted: '#c2d2e5'
};

export const rgb = hex => hex.slice(1).match(/.{2}/g).map(value => parseInt(value, 16)).join(', ');
export const withAlpha = (hex, opacity) => `rgba(${rgb(hex)}, ${opacity})`;
export const chartColors = [
  colors.primary, colors.accent, '#6b5ca5', '#b17b2d', '#337f9b',
  '#6b7c49', '#a14b70', '#586e99', '#37756c', '#956744'
];

const variants = {
  primary: { base: colors.primary, hover: colors.primaryHover },
  secondary: { base: colors.secondary, hover: colors.secondaryHover },
  success: { base: colors.success, hover: colors.successHover },
  info: { base: colors.accent, hover: colors.accentHover },
  warning: { base: colors.warning, hover: colors.warningHover, text: colors.ink, emphasis: colors.warningText },
  danger: { base: colors.danger, hover: colors.dangerHover }
};

const variables = {
  ...Object.fromEntries(Object.entries(colors).map(([name, value]) => [
    `--bw-${name.replace(/[A-Z]/g, letter => `-${letter.toLowerCase()}`)}`, value
  ])),
  '--bs-body-font-family': '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  '--bs-body-color': colors.ink,
  '--bs-body-color-rgb': rgb(colors.ink),
  '--bs-heading-color': colors.ink,
  '--bs-body-bg': colors.background,
  '--bs-body-bg-rgb': rgb(colors.background),
  '--bs-secondary-color': colors.muted,
  '--bs-secondary-color-rgb': rgb(colors.muted),
  '--bs-tertiary-bg': colors.surfaceTint,
  '--bs-border-color': colors.border,
  '--bs-link-color': colors.primary,
  '--bs-link-color-rgb': rgb(colors.primary),
  '--bs-link-hover-color': colors.primaryHover,
  '--bs-link-hover-color-rgb': rgb(colors.primaryHover),
  '--bs-light-rgb': rgb(colors.surfaceTint),
  '--bs-dark-rgb': rgb(colors.navy),
  '--bs-focus-ring-color': withAlpha(colors.primary, 0.25),
  '--toastify-font-family': '"Inter", sans-serif',
  '--toastify-color-info': colors.primary,
  '--toastify-color-success': colors.success,
  '--toastify-color-warning': colors.warning,
  '--toastify-color-error': colors.danger,
  '--toastify-text-color-light': colors.ink
};

export const globalThemeStyles = { ':root': variables };
Object.entries(variants).forEach(([name, variant]) => {
  variables[`--bs-${name}`] = variant.base;
  variables[`--bs-${name}-rgb`] = rgb(variant.base);
  variables[`--bs-${name}-text-emphasis`] = variant.emphasis || variant.hover;
  variables[`--bs-${name}-bg-subtle`] = withAlpha(variant.base, 0.08);
  variables[`--bs-${name}-border-subtle`] = withAlpha(variant.base, 0.24);
  globalThemeStyles[`.btn-${name}`] = {
    '--bs-btn-color': variant.text || colors.surface,
    '--bs-btn-bg': variant.base,
    '--bs-btn-border-color': variant.base,
    '--bs-btn-hover-color': variant.text || colors.surface,
    '--bs-btn-hover-bg': variant.hover,
    '--bs-btn-hover-border-color': variant.hover,
    '--bs-btn-active-color': variant.text || colors.surface,
    '--bs-btn-active-bg': variant.hover,
    '--bs-btn-active-border-color': variant.hover,
    '--bs-btn-disabled-color': variant.text || colors.surface,
    '--bs-btn-disabled-bg': variant.base,
    '--bs-btn-disabled-border-color': variant.base,
    '--bs-btn-focus-shadow-rgb': rgb(variant.base)
  };
  globalThemeStyles[`.btn-outline-${name}`] = {
    '--bs-btn-color': variant.emphasis || variant.base,
    '--bs-btn-border-color': variant.emphasis || variant.base,
    '--bs-btn-hover-color': variant.text || colors.surface,
    '--bs-btn-hover-bg': variant.base,
    '--bs-btn-hover-border-color': variant.base,
    '--bs-btn-active-color': variant.text || colors.surface,
    '--bs-btn-active-bg': variant.hover,
    '--bs-btn-active-border-color': variant.hover,
    '--bs-btn-disabled-color': variant.emphasis || variant.base,
    '--bs-btn-disabled-border-color': variant.emphasis || variant.base,
    '--bs-btn-focus-shadow-rgb': rgb(variant.base)
  };
});

export const bomwiseTheme = createTheme({
  palette: {
    mode: 'light',
    primary: { main: colors.primary, dark: colors.primaryHover, contrastText: colors.surface },
    secondary: { main: colors.accent, dark: colors.accentHover, contrastText: colors.surface },
    success: { main: colors.success },
    warning: { main: colors.warning, contrastText: colors.ink },
    error: { main: colors.danger },
    info: { main: colors.accent },
    text: { primary: colors.ink, secondary: colors.muted },
    background: { default: colors.background, paper: colors.surface },
    divider: colors.border
  },
  typography: {
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    button: { textTransform: 'none', fontWeight: 600 }
  },
  shape: { borderRadius: 10 },
  components: {
    MuiButton: { styleOverrides: { root: { borderRadius: 8 } } },
    MuiOutlinedInput: {
      styleOverrides: { notchedOutline: { borderColor: colors.controlBorder } }
    },
    MuiTooltip: {
      styleOverrides: { tooltip: { backgroundColor: colors.navy, color: colors.surface } }
    },
    MuiDataGrid: {
      styleOverrides: {
        root: {
          '--DataGrid-containerBackground': colors.surfaceTint,
          backgroundColor: colors.surface,
          borderColor: colors.border,
          borderRadius: 10,
          '& .MuiDataGrid-columnHeader': { backgroundColor: colors.surfaceTint, color: colors.ink },
          '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 600 },
          '& .MuiDataGrid-row:hover': { backgroundColor: withAlpha(colors.primary, 0.045) },
          '& .MuiDataGrid-row.Mui-selected': { backgroundColor: withAlpha(colors.primary, 0.1) },
          '& .MuiDataGrid-row.Mui-selected:hover': { backgroundColor: withAlpha(colors.primary, 0.15) },
          '& .MuiDataGrid-cell': { borderColor: colors.border },
          '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within, & .MuiDataGrid-columnHeader:focus-within': {
            outline: `2px solid ${colors.primary}`, outlineOffset: -2
          },
          '& .MuiDataGrid-cell--editing': { backgroundColor: colors.surface },
          '& .MuiDataGrid-toolbarContainer': {
            padding: '8px 12px', borderBottom: `1px solid ${colors.border}`
          },
          '& .MuiDataGrid-footerContainer': { backgroundColor: colors.surfaceTint, borderColor: colors.border }
        }
      }
    }
  }
});
